// cameras.js, curated static live feed config.
// Availability is checked by the player at runtime; oEmbed is not a playback guarantee.
// Removed the private uwXgcTc8oY8 default during the 2026-10-05 review.
// NASA content: public domain, US government work.
// Avid Space (formerly LabPadre): YouTube embed license, dev/MVP use. Get written permission before commercial launch.
// NASASpaceflight: YouTube embed license, dev/MVP use. Get written permission before commercial launch.

export const FEEDS = [
  // ── NASA ─────────────────────────────────────────────────────────────────
  {
    id: 'nasa-iss-hd',
    label: 'ISS HD Earth Views',
    provider: 'NASA',
    ytId: 'awQzjn72bI0',
    tags: ['iss', 'nasa'],
    note: 'Official NASA live Earth views from the ISS.',
  },
  {
    id: 'nasa-iss-live',
    label: 'ISS Live',
    provider: 'NASA',
    ytId: 'M3HKLzjvKPc',
    tags: ['iss', 'nasa'],
    note: 'Live station views and operations; coverage varies.',
  },

  // ── Avid Space (formerly LabPadre), Starbase, TX ─────────────────────────
  {
    id: 'avidspace-starbase-multiplex',
    label: 'Starbase Multi-Plex',
    provider: 'Avid Space',
    ytId: 'XFmmS5BLlbE',
    tags: ['starbase', 'starship'],
    note: 'Multiple PTZ cams. Best overview of OLP-A and OLP-B.',
  },
  {
    id: 'avidspace-starbase-rover',
    label: 'Starbase Rover Cam',
    provider: 'Avid Space',
    ytId: 'inCQ49QGmqQ',
    tags: ['starbase', 'starship'],
    note: 'Mobile ground-level view of the launch complex.',
  },
  {
    id: 'avidspace-starbase-nerdle',
    label: 'Starbase Nerdle Cam',
    provider: 'Avid Space',
    ytId: 'B89oRmywnJo',
    tags: ['starbase', 'starship'],
    note: 'Fixed pad-facing view.',
  },

  // ── NASASpaceflight, Starbase, TX ────────────────────────────────────────
  {
    id: 'nsf-starbase',
    label: 'Starbase Live 24/7',
    provider: 'NASASpaceflight',
    ytId: 'mhJRzQsLZGg',
    tags: ['starbase', 'starship'],
    note: '24/7 Starship and Super Heavy development.',
  },

  // ── Avid Space, Cape Canaveral / KSC ─────────────────────────────────────
  {
    id: 'avidspace-cape',
    label: 'Cape Cam, KSC',
    provider: 'Avid Space',
    ytId: 'XTNRGKHiZNg',
    tags: ['cape', 'falcon', 'ksc'],
    note: 'SLC-40 and LC-39A pad views.',
  },
  {
    id: 'avidspace-portside',
    label: 'Port Canaveral, Portside',
    provider: 'Avid Space',
    ytId: 'tqO3cQELieE',
    tags: ['cape', 'falcon', 'booster'],
    note: 'Drone Ship and booster recovery port.',
  },

  // ── NASASpaceflight, Space Coast / KSC ───────────────────────────────────
  {
    id: 'nsf-space-coast',
    label: 'Space Coast Live',
    provider: 'NASASpaceflight',
    ytId: 'Jm8wRjD3xVA',
    tags: ['cape', 'falcon', 'ksc'],
    note: '24/7 KSC and Cape Canaveral. Covers LC-39A Starship pad.',
  },

  // ── NASASpaceflight, McGregor, TX ────────────────────────────────────────
  {
    id: 'nsf-mcgregor',
    label: 'McGregor Engine Testing',
    provider: 'NASASpaceflight',
    ytId: 'cOmmvhDQ2HM',
    tags: ['mcgregor', 'engines', 'starship'],
    note: '24/7 SpaceX Raptor and Merlin engine test facility.',
  },
];

// Map of NORAD → feed IDs to surface first when that object is selected
export const NORAD_FEEDS = {
  25544: ['nasa-iss-hd', 'nasa-iss-live'],  // ISS
};

// Map of pad keywords → feed IDs to surface first for launches at that pad
// Matched against pad_name / pad_location from Launch Library 2
export const PAD_FEEDS = {
  starbase:     ['avidspace-starbase-multiplex', 'nsf-starbase', 'avidspace-starbase-rover', 'avidspace-starbase-nerdle'],
  'boca chica': ['avidspace-starbase-multiplex', 'nsf-starbase', 'avidspace-starbase-rover', 'avidspace-starbase-nerdle'],
  'slc-40':     ['avidspace-cape', 'nsf-space-coast', 'avidspace-portside'],
  'lc-39a':     ['nsf-space-coast', 'avidspace-cape', 'avidspace-portside'],
  'kennedy':    ['nsf-space-coast', 'avidspace-cape', 'avidspace-portside'],
  'canaveral':  ['avidspace-cape', 'nsf-space-coast', 'avidspace-portside'],
  vandenberg:   [],  // no cam there yet
};

// Given a selected satellite/launch object, return feeds ordered by relevance.
// Falls back to all feeds in default order if no specific match.
export function feedsForContext(selected) {
  if (!selected) return FEEDS;

  // NORAD match (ISS, Tiangong, etc.)
  const noradMatch = selected.norad_id && NORAD_FEEDS[selected.norad_id];
  if (noradMatch) {
    const priority = noradMatch.map(id => FEEDS.find(f => f.id === id)).filter(Boolean);
    const rest     = FEEDS.filter(f => !noradMatch.includes(f.id));
    return [...priority, ...rest];
  }

  // Launch pad match
  if (selected.category === 'launches' && selected.launch) {
    const padStr = [selected.launch.pad_name, selected.launch.pad_location]
      .filter(Boolean).join(' ').toLowerCase();
    for (const [keyword, ids] of Object.entries(PAD_FEEDS)) {
      if (padStr.includes(keyword) && ids.length > 0) {
        const priority = ids.map(id => FEEDS.find(f => f.id === id)).filter(Boolean);
        const rest     = FEEDS.filter(f => !ids.includes(f.id));
        return [...priority, ...rest];
      }
    }
  }

  return FEEDS;
}

// Recovery stays with the selected publisher; metadata alone cannot prove playback.
export function providerDirectory(feed) {
  const provider = feed.provider || feed.publisher;
  if (provider === 'NASA') return {label: 'NASA live directory', url: 'https://www.nasa.gov/live/'};
  if (provider === 'Avid Space') return {label: 'Avid Space live feeds', url: 'https://www.youtube.com/@LabPadre/streams'};
  if (provider === 'NASASpaceflight') return {label: 'NSF live feeds', url: 'https://nsf.live/'};
  return null;
}
